/**
 * Utility functions for filtering and cleaning AI message content
 */

/**
 * Removes the "Sources:" footer from AI responses that includes file references
 * Format: "Sources: [1] Referenced content (File: file-xxxxx) [2] Referenced content (File: file-xxxxx)"
 * @param content - The AI message content
 * @returns Cleaned content without the sources footer
 */
export function removeSourcesFooter(content: string): string {
  // Pattern to match "Sources:" followed by file references
  // This handles various formats of source citations with file IDs
  const sourcesPattern = /\n?\s*Sources:\s*\[\d+\][\s\S]*?\(File:\s*file-[^\)]+\)[\s\S]*?$/i;
  
  // Also handle cases where it might start on a new line without "Sources:" prefix
  const fileReferencesPattern = /\n?\s*\[\d+\]\s+.*?\(File:\s*file-[^\)]+\).*$/i;
  
  // Handle edge cases where "Source:" or "Sources:" appears without proper file references
  // This removes "Source:" or "Sources:" when it's at the end with minimal or no content after
  const emptySourcePattern = /\n?\s*Sources?:\s*[\s\n]*$/i;
  
  let cleanedContent = content.replace(sourcesPattern, '');
  cleanedContent = cleanedContent.replace(fileReferencesPattern, '');
  
  // Remove empty "Source:" or "Sources:" at the end
  cleanedContent = cleanedContent.replace(emptySourcePattern, '');
  
  // Handle markdown-formatted sources that might be empty
  const markdownSourcePattern = /\n?\s*\*\*Sources?:\*\*\s*$/i;
  cleanedContent = cleanedContent.replace(markdownSourcePattern, '');
  
  // Check if there's a hanging "Source:" with minimal content that doesn't look like proper citations
  // Remove "Source:" if it's not followed by proper citation format within reasonable distance
  const hangingSourcePattern = /\n?\s*Sources?:\s*(?!\[\d+\]|\*\*|[A-Za-z0-9]{3,})/i;
  cleanedContent = cleanedContent.replace(hangingSourcePattern, '');
  
  // Trim any trailing whitespace
  return cleanedContent.trim();
}

/**
 * Checks if content contains source references that should be filtered
 * @param content - The message content to check
 * @returns True if content contains source references
 */
export function hasSourceReferences(content: string): boolean {
  // Input validation
  if (!content || typeof content !== 'string') {
    return false;
  }
  
  // Check for proper source citations with file references
  const sourcesPattern = /Sources:\s*\[\d+\]/i;
  const filePattern = /\(File:\s*file-[^\)]+\)/i;
  
  // Check for empty or hanging "Source:" patterns that should be removed
  const emptySourcePattern = /Sources?:\s*$/i;
  const hangingSourcePattern = /Sources?:\s*(?!\[\d+\]|\*\*|[A-Za-z0-9]{3,})/i;
  
  return sourcesPattern.test(content) || 
         filePattern.test(content) || 
         emptySourcePattern.test(content) || 
         hangingSourcePattern.test(content);
} 